/**
 * SVG path geometry for the canvas edges. Pure functions, so the routing is testable without
 * a browser — the edge components only hand React Flow's coordinates in and draw the string.
 */
import type { LineStyle } from './lineStyle';

export interface Pt {
  x: number;
  y: number;
}

/** Corner rounding. Purely cosmetic — square corners read as a routing glitch. */
const R = 8;

/**
 * Rounded polyline through `pts`.
 *
 * Each interior point becomes a quadratic corner, clamped to half the shorter adjacent
 * segment so a tight jog cannot produce a loop. (Moved here from SkipEdge, which still uses
 * it, so the detour and the eligibility wires turn corners the same way.)
 */
export function roundedPath(pts: Pt[], radius = R): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const next = pts[i + 1];
    const inLen = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const outLen = Math.hypot(next.x - cur.x, next.y - cur.y);
    const r = Math.max(0, Math.min(radius, inLen / 2, outLen / 2));
    const inUx = inLen ? (cur.x - prev.x) / inLen : 0;
    const inUy = inLen ? (cur.y - prev.y) / inLen : 0;
    const outUx = outLen ? (next.x - cur.x) / outLen : 0;
    const outUy = outLen ? (next.y - cur.y) / outLen : 0;
    d += ` L ${cur.x - inUx * r},${cur.y - inUy * r}`;
    d += ` Q ${cur.x},${cur.y} ${cur.x + outUx * r},${cur.y + outUy * r}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last.x},${last.y}`;
  return d;
}

/** Horizontal S-curve between two points — the shape of every "fan-out" line. */
export function hCurve(from: Pt, to: Pt): string {
  const dx = Math.max(40, (to.x - from.x) * 0.45);
  return `M ${from.x},${from.y} C ${from.x + dx},${from.y} ${to.x - dx},${to.y} ${to.x},${to.y}`;
}

/**
 * Where a question→rule wire runs, as decided by the projection.
 *
 * `lane` is absent when nothing stands between the card and the rule column; then the wire
 * goes straight across. Otherwise the wire leaves the card's right edge, drops down the
 * column gap at `lane.gapX`, runs along the card-free strip between two rows at `lane.y`,
 * and leaves the strip at `lane.exitX`, which is past the last card.
 */
export interface RuleWireLane {
  gapX: number;
  y: number;
  exitX: number;
}

/**
 * The points of a question→rule wire up to where it leaves the question area — the part that
 * is the SAME in both line styles (only the final hop into the rule label differs). Exported
 * for the "never crosses a card" test.
 *
 * Without a lane the wire runs straight right at the card's mid-height to `exitX`: the
 * projection only omits the lane when no card overlaps that height to the right.
 */
export function ruleWirePoints(from: Pt, exitX: number, lane?: RuleWireLane): Pt[] {
  if (!lane) return [from, { x: exitX, y: from.y }];
  return [from, { x: lane.gapX, y: from.y }, { x: lane.gapX, y: lane.y }, { x: lane.exitX, y: lane.y }];
}

export function ruleWirePath(
  from: Pt,
  to: Pt,
  lane: RuleWireLane | undefined,
  exitX: number,
  style: LineStyle,
): string {
  const pts = ruleWirePoints(from, exitX, lane);
  const exit = pts[pts.length - 1];
  if (style === 'dev') {
    // Right angles through the gap and the strip, then a curve into the rule label.
    const head = pts.length > 2 ? roundedPath(pts, 6) : `M ${from.x},${from.y} L ${exit.x},${exit.y}`;
    return `${head} C ${exit.x + 30},${exit.y} ${to.x - 30},${to.y} ${to.x},${to.y}`;
  }
  // Orthogonal all the way, like the navigation edges: a vertical jog in the open space
  // between the question area and the rule column, then straight into the label.
  const jogX = exit.x + 20;
  if (Math.abs(exit.y - to.y) < 1) {
    // Already level with the label: an unrouted wire is one straight segment.
    return pts.length === 2 ? `M ${from.x},${from.y} L ${to.x},${to.y}` : roundedPath([...pts, to]);
  }
  return roundedPath([...pts, { x: jogX, y: exit.y }, { x: jogX, y: to.y }, to]);
}

/**
 * `?mode=dev` only: question→question as the prototype draws it — a vertical S-curve, and a
 * row-skipping edge as a bow out to the left lane instead of a right-angled detour.
 */
export function devFlowPath(from: Pt, to: Pt, laneX?: number): string {
  if (laneX === undefined) {
    const my = (from.y + to.y) / 2;
    return `M ${from.x},${from.y} C ${from.x},${my} ${to.x},${my} ${to.x},${to.y}`;
  }
  const my = (from.y + to.y) / 2;
  return `M ${from.x},${from.y} C ${from.x},${from.y + 40} ${laneX},${from.y + 40} ${laneX},${my} S ${to.x},${to.y - 40} ${to.x},${to.y}`;
}
